import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTrashAlt, faPlus, faTimes, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";
import riskApi from "../../api/risk";

// ─────────────────────────────────────────────────────────
// Shared Input Field (with green checkmark icon)
// ─────────────────────────────────────────────────────────
const LimitField = ({ label, value, onChange, type = "number", placeholder = "0" }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '10px 40px 10px 14px',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          fontSize: '0.92rem',
          color: '#333',
          outline: 'none',
          boxSizing: 'border-box',
          background: '#fff'
        }}
      />
      <FontAwesomeIcon
        icon={faCheck}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#28A745', fontSize: '1rem' }}
      />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────
// Tab Button
// ─────────────────────────────────────────────────────────
const TabBtn = ({ label, active, onClick, color }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: '5px 8px',
      border: 'none',
      borderRadius: '4px',
      background: color,
      color: color === '#FFC107' ? '#212529' : '#fff',
      fontWeight: '400',
      fontSize: '0.82rem',
      cursor: 'pointer',
      opacity: active ? 1 : 0.75,
      transition: 'opacity 0.2s',
      lineHeight: '1.4'
    }}
  >
    {label}
  </button>
);

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────
const GAME_TYPES = [
  { value: 'BORLETTE', label: 'Borlette' },
  { value: 'LOTO3',    label: 'Loto 3' },
  { value: 'LOTO4',    label: 'Loto 4' },
  { value: 'LOTO5',    label: 'Loto 5' },
  { value: 'MARIAGE',  label: 'Mariage' },
];

const sInput = {
  padding: '7px 10px', border: '1px solid #dee2e6', borderRadius: '5px',
  fontSize: '0.85rem', outline: 'none', width: '100%', boxSizing: 'border-box'
};
const sSelect = { ...sInput };
const sLabel = { display: 'block', fontSize: '0.78rem', fontWeight: '600', marginBottom: '3px', color: '#555' };
const sTh = { padding: '10px 12px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
const sTd = { padding: '9px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle', fontSize: '0.88rem' };
const sThLast = { ...sTh, borderRight: 'none' };
const sTdLast = { ...sTd, borderRight: 'none' };


// ─────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────
export default function Limites() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ── Général state ───────────────────────────────────────
  const [globalLimits, setGlobalLimits] = useState({});
  const [form, setForm] = useState({
    borlette: 0, loto3: 0, mariage: 0, loto4: 0, loto5: 0
  });
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);

  // ── Agent tab state ─────────────────────────────────────
  const [agents,         setAgents]         = useState([]);
  const [draws,          setDraws]           = useState([]);
  const [agentLimits,    setAgentLimits]     = useState([]);
  const [showAddAgent,   setShowAddAgent]    = useState(false);
  const [editingAgent,   setEditingAgent]    = useState(null);   // id being edited
  const [editAgentData,  setEditAgentData]   = useState({});
  const [newAgentLimit,  setNewAgentLimit]   = useState({
    agent: '', game_type: 'BORLETTE', draw: '', number_limit: ''
  });


  // ── Fiche tab state ─────────────────────────────────────
  const [ficheLimits,    setFicheLimits]    = useState([]);
  const [showAddFiche,   setShowAddFiche]   = useState(false);
  const [editingFiche,   setEditingFiche]   = useState(null);
  const [editFicheData,  setEditFicheData]  = useState({});
  const [newFicheLimit,  setNewFicheLimit]  = useState({
    game_type: 'BORLETTE', draw: '', min_bet: '0', max_bet: '5000'
  });

  // ── Notifications ───────────────────────────────────────
  const showSuccess = msg => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 3500); };
  const showError   = msg => { setErrorMsg(msg);   setTimeout(() => setErrorMsg(""),   5000); };

  // ── Fetch ───────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [glRes, ggRes, agRes, drRes, expAgentRes, blRes, expGenRes] = await Promise.all([
        client("risk/global-limits/"),
        client("grille-gains/?target_level=TENANT"),
        client("agents/"),
        client("sales/draws/"),
        riskApi.getExposureLimits("AGENT"),
        riskApi.getNumberOverrides(),
        riskApi.getExposureLimits("GENERAL"),
      ]);

      // GlobalLimits map
      const glList = glRes.results || (Array.isArray(glRes) ? glRes : []);
      const glMap  = {};
      glList.forEach(g => { glMap[g.game_type] = g; });
      setGlobalLimits(glMap);

      setForm({
        borlette: glMap['BORLETTE']?.default_number_limit ?? 0,
        loto3:    glMap['LOTO3']?.default_number_limit    ?? 0,
        mariage:  glMap['MARIAGE']?.default_number_limit  ?? 0,
        loto4:    glMap['LOTO4']?.default_number_limit    ?? 0,
        loto5:    glMap['LOTO5']?.default_number_limit    ?? 0,
      });

      // Agents
      const agList = agRes.results || (Array.isArray(agRes) ? agRes : []);
      setAgents(agList);

      // Draws - API returns array directly for sales/draws
      const drList = Array.isArray(drRes) ? drRes : (drRes.results || []);
      setDraws(drList);

      // Agent limits
      setAgentLimits(Array.isArray(expAgentRes) ? expAgentRes : (expAgentRes.results || []));

      // Blocked numbers
      const blList = blRes.results || (Array.isArray(blRes) ? blRes : []);

      // General/Fiche limits
      const feList = expGenRes.results || (Array.isArray(expGenRes) ? expGenRes : []);
      setFicheLimits(feList);

    } catch (err) {
      console.error("fetchAll error:", err);
      setErrorMsg("Erreur lors du chargement.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── GÉNÉRAL: Save ───────────────────────────────────────
  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      for (const { key, field } of [
        { key: 'BORLETTE', field: 'borlette' },
        { key: 'LOTO3',    field: 'loto3' },
        { key: 'MARIAGE',  field: 'mariage' },
        { key: 'LOTO4',    field: 'loto4' },
        { key: 'LOTO5',    field: 'loto5' },
      ]) {
        const existing = globalLimits[key];
        const val = parseFloat(form[field]) || 0;
        if (existing?.id) {
          await client(`risk/global-limits/${existing.id}/`, { method: 'PATCH', body: { default_number_limit: val } });
        } else {
          await client('risk/global-limits/', { method: 'POST', body: { game_type: key, default_number_limit: val, max_ticket_amount: 50000, max_draw_exposure: 500000 } });
        }
      }
      showSuccess("Limites générales sauvegardées !");
      setIsEditingGeneral(false);
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || err?.message || JSON.stringify(err)));
    }
    setSaving(false);
  };

  const handleDeleteGeneralLimit = async (key, field) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette limite ?")) return;
    setSaving(true);
    try {
      if (['BORLETTE', 'LOTO3', 'MARIAGE', 'LOTO4', 'LOTO5'].includes(key)) {
        const existing = globalLimits[key];
        if (existing?.id) {
          await client(`risk/global-limits/${existing.id}/`, { method: 'PATCH', body: { default_number_limit: 0 } });
        }
      }
      showSuccess("Limite générale supprimée !");
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || err?.message || JSON.stringify(err)));
    }
    setSaving(false);
  };

  // ── AGENT: Add ──────────────────────────────────────────────────
  const handleAddAgentLimit = async e => {
    e.preventDefault();
    if (!newAgentLimit.agent) { showError("Sélectionnez un agent."); return; }
    if (!newAgentLimit.number_limit || parseFloat(newAgentLimit.number_limit) <= 0) {
      showError("Saisissez une limite par numéro supérieure à 0."); return;
    }
    setSaving(true);
    try {
      const payload = {
        target_level: 'AGENT',
        agent: newAgentLimit.agent,
        game_type: newAgentLimit.game_type,
        number_limit: parseFloat(newAgentLimit.number_limit),
        is_active: true,
      };
      if (newAgentLimit.draw) payload.draw = newAgentLimit.draw;
      await riskApi.createExposureLimit(payload);
      showSuccess("Limite agent ajoutée !");
      setNewAgentLimit({ agent: '', game_type: 'BORLETTE', draw: '', number_limit: '' });
      setShowAddAgent(false);
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || err?.non_field_errors?.[0] || JSON.stringify(err)));
    }
    setSaving(false);
  };

  // ── AGENT: Edit ────────────────────────────────────────
  const startEditAgent = limit => {
    setEditingAgent(limit.id);
    setEditAgentData({ number_limit: limit.number_limit, is_active: limit.is_active });
  };
  const cancelEditAgent = () => { setEditingAgent(null); setEditAgentData({}); };
  const saveEditAgent = async () => {
    setSaving(true);
    try {
      await riskApi.updateExposureLimit(editingAgent, editAgentData);
      showSuccess("Limite mise à jour !");
      setEditingAgent(null);
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || JSON.stringify(err)));
    }
    setSaving(false);
  };

  // ── AGENT: Delete ──────────────────────────────────────
  const handleDeleteAgent = async id => {
    if (!window.confirm("Supprimer cette limite ?")) return;
    try { await riskApi.deleteExposureLimit(id); fetchAll(); }
    catch (err) { showError("Erreur suppression."); }
  };



  // ── FICHE: Add ─────────────────────────────────────────
  const handleAddFicheLimit = async e => {
    e.preventDefault();
    if (!newFicheLimit.max_bet) { showError("Saisissez une limite maximum."); return; }
    setSaving(true);
    try {
      const payload = {
        target_level: 'GENERAL',
        game_type: newFicheLimit.game_type,
        min_bet: parseFloat(newFicheLimit.min_bet) || 0,
        max_bet: parseFloat(newFicheLimit.max_bet),
        is_active: true,
      };
      if (newFicheLimit.draw) payload.draw = newFicheLimit.draw;
      await riskApi.createExposureLimit(payload);
      showSuccess("Limite ajoutée !");
      setNewFicheLimit({ game_type: 'BORLETTE', draw: '', min_bet: '0', max_bet: '5000' });
      setShowAddFiche(false);
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || JSON.stringify(err)));
    }
    setSaving(false);
  };

  // ── FICHE: Edit ────────────────────────────────────────
  const startEditFiche = limit => {
    setEditingFiche(limit.id);
    setEditFicheData({ min_bet: limit.min_bet, max_bet: limit.max_bet, is_active: limit.is_active });
  };
  const cancelEditFiche = () => { setEditingFiche(null); setEditFicheData({}); };
  const saveEditFiche = async () => {
    setSaving(true);
    try {
      await riskApi.updateExposureLimit(editingFiche, editFicheData);
      showSuccess("Limite mise à jour !");
      setEditingFiche(null);
      fetchAll();
    } catch (err) {
      showError("Erreur : " + (err?.detail || JSON.stringify(err)));
    }
    setSaving(false);
  };

  // ── FICHE: Delete ──────────────────────────────────────
  const handleDeleteFiche = async id => {
    if (!window.confirm("Supprimer cette limite ?")) return;
    try { await riskApi.deleteExposureLimit(id); fetchAll(); }
    catch (err) { showError("Erreur suppression."); }
  };


  // ──────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <Spinner animation="border" variant="primary" />
      <p style={{ marginTop: '16px', color: '#666' }}>Chargement des limites...</p>
    </div>
  );

  const agentName = a => a.username || `${a.first_name || ''} ${a.last_name || ''}`.trim() || `Agent #${a.id}`;

  return (
    <div style={{ padding: '0 0 40px' }}>

      {/* ── Notifications ── */}
      {successMsg && (
        <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '9px 14px', borderRadius: '6px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.88rem' }}>
          <span><FontAwesomeIcon icon={faCheck} style={{ marginRight: '8px' }} />{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#155724' }}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
      )}
      {errorMsg && (
        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '9px 14px', borderRadius: '6px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.88rem' }}>
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg("")} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#721c24' }}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
      )}

      {/* ── Tab Buttons ── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
        <TabBtn label="Général"               active={activeTab === 'general'} color="#007BFF" onClick={() => setActiveTab('general')} />
        <TabBtn label="Agent"                 active={activeTab === 'agent'}   color="#28A745" onClick={() => setActiveTab('agent')} />
        <TabBtn label="Limite par fiche"      active={activeTab === 'fiche'}   color="#DC3545" onClick={() => setActiveTab('fiche')} />
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* GÉNÉRAL                                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === 'general' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h4 style={{ fontWeight: '700', margin: 0 }}>Général</h4>
            {!isEditingGeneral && (
              <button
                onClick={() => setIsEditingGeneral(true)}
                style={{ padding: '6px 14px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
              >
                Modifier
              </button>
            )}
          </div>

          {!isEditingGeneral ? (
            <div>
              {(() => {
                const generalLimitItems = [
                  { label: "Borlette", value: form.borlette, key: 'BORLETTE', field: 'borlette' },
                  { label: "Loto 3", value: form.loto3, key: 'LOTO3', field: 'loto3' },
                  { label: "Mariage", value: form.mariage, key: 'MARIAGE', field: 'mariage' },
                  { label: "Loto 4", value: form.loto4, key: 'LOTO4', field: 'loto4' },
                  { label: "Loto 5", value: form.loto5, key: 'LOTO5', field: 'loto5' },
                ];
                const definedLimits = generalLimitItems.filter(item => parseFloat(item.value) > 0);

                if (definedLimits.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', border: '1px dashed #dee2e6', color: '#666' }}>
                      <p style={{ fontWeight: '600', margin: 0 }}>Aucune limite générale configurée.</p>
                      <p style={{ fontSize: '0.82rem', margin: '4px 0 0', color: '#888' }}>Cliquez sur Modifier pour définir des limites.</p>
                    </div>
                  );
                }

                return (
                  <div style={{ overflowX: 'auto', border: '1px solid #dee2e6' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...sTh, borderRight: '1px solid #dee2e6' }}>TYPE</th>
                          <th style={{ ...sTh, textAlign: 'right', borderRight: '1px solid #dee2e6' }}>LIMITE</th>
                          <th style={{ ...sThLast, textAlign: 'center', width: '100px' }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {definedLimits.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ ...sTd, fontWeight: '700', borderRight: '1px solid #dee2e6', color: '#1D1D1F' }}>
                              {item.label.toUpperCase()}
                            </td>
                            <td style={{ ...sTd, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', fontSize: '1rem', color: '#212529', borderRight: '1px solid #dee2e6' }}>
                              {Number(item.value).toLocaleString()} HTG
                            </td>
                            <td style={{ ...sTdLast, textAlign: 'center' }}>
                              <button
                                onClick={() => handleDeleteGeneralLimit(item.key, item.field)}
                                title="Supprimer"
                                style={{ padding: '5px 9px', background: 'none', border: '1px solid #dc3545', borderRadius: '4px', color: '#dc3545', cursor: 'pointer' }}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 24px' }}>
                <LimitField label="Borlette" value={form.borlette} onChange={v => setForm(f => ({ ...f, borlette: v }))} />
                <LimitField label="Loto 3"   value={form.loto3}    onChange={v => setForm(f => ({ ...f, loto3: v }))} />
                <LimitField label="Mariage"  value={form.mariage}  onChange={v => setForm(f => ({ ...f, mariage: v }))} />
                <LimitField label="Loto 4" value={form.loto4} onChange={v => setForm(f => ({ ...f, loto4: v }))} />
                <LimitField label="Loto 5" value={form.loto5} onChange={v => setForm(f => ({ ...f, loto5: v }))} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <button
                  onClick={handleSaveGeneral}
                  disabled={saving}
                  style={{ flex: 1, padding: '12px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.75 : 1 }}
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button
                  onClick={() => setIsEditingGeneral(false)}
                  disabled={saving}
                  style={{ padding: '12px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* AGENT                                                      */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === 'agent' && (
        <div>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ fontWeight: '700', margin: 0 }}>Limites par Agent</h4>
            <button
              onClick={() => setShowAddAgent(v => !v)}
              style={{ padding: '6px 14px', background: showAddAgent ? '#6c757d' : '#28A745', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <FontAwesomeIcon icon={showAddAgent ? faTimes : faPlus} />
              {showAddAgent ? 'Annuler' : 'Ajouter'}
            </button>
          </div>

          {/* Add form */}
          {showAddAgent && (
            <form onSubmit={handleAddAgentLimit} style={{ background: '#f8f9fa', border: '1px solid #e9ecef', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'end' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={sLabel}>Agent *</label>
                  <select style={sSelect} value={newAgentLimit.agent} onChange={e => { const val = e.target.value; setNewAgentLimit(f => ({ ...f, agent: val })); }} required>
                    <option value="">Sélectionner un agent...</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{agentName(a)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={sLabel}>Jeu</label>
                  <select style={sSelect} value={newAgentLimit.game_type} onChange={e => { const val = e.target.value; setNewAgentLimit(f => ({ ...f, game_type: val })); }}>
                    {GAME_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={sLabel}>Tirage (opt.)</label>
                  <select style={sSelect} value={newAgentLimit.draw} onChange={e => { const val = e.target.value; setNewAgentLimit(f => ({ ...f, draw: val })); }}>
                    <option value="">Tous les tirages</option>
                    {draws.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: 'span 3' }}>
                  <label style={sLabel}>Limite par numéro (HTG) *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number" min="1" step="1"
                      style={{ ...sInput, paddingRight: '80px' }}
                      value={newAgentLimit.number_limit}
                      onChange={e => { const val = e.target.value; setNewAgentLimit(f => ({ ...f, number_limit: val })); }}
                      placeholder="ex: 250"
                      required
                    />
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.78rem', color: '#888', fontWeight: '600' }}>HTG/n°</span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6c757d' }}>
                    Chaque numéro (00–99) est coupé automatiquement dès que cet agent atteint ce montant cumulé.
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="submit" disabled={saving} style={{ width: '100%', padding: '9px', background: '#28A745', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: '600', fontSize: '0.88rem', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />Enregistrer
                  </button>
                </div>
              </div>
            </form>
          )}

          <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={sTh}>Agent</th>
                  <th style={sTh}>Jeu</th>
                  <th style={sTh}>Tirage</th>
                  <th style={{ ...sTh, textAlign: 'right' }}>Limite / Numéro (HTG)</th>
                  <th style={{ ...sTh, textAlign: 'center' }}>Statut</th>
                  <th style={{ ...sTh, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {agentLimits.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...sTd, textAlign: 'center', color: '#999', padding: '28px' }}>Aucune limite par agent définie.</td></tr>
                ) : agentLimits.map(limit => (
                  <tr key={limit.id} style={{ background: editingAgent === limit.id ? '#f0f7ff' : '#fff' }}>
                    <td style={{ ...sTd, fontWeight: '600' }}>{limit.agent_name || `Agent #${limit.agent}`}</td>
                    <td style={sTd}>{limit.game_type_display || limit.game_type}</td>
                    <td style={{ ...sTd, color: '#666' }}>{limit.draw_name || 'Tous'}</td>

                    {editingAgent === limit.id ? (
                      <>
                        <td style={sTd}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number" min="1"
                              value={editAgentData.number_limit ?? ''}
                              onChange={e => { const val = e.target.value; setEditAgentData(d => ({ ...d, number_limit: val })); }}
                              style={{ ...sInput, width: '120px', textAlign: 'right', paddingRight: '48px' }}
                            />
                            <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem', color: '#888' }}>HTG</span>
                          </div>
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                            <input type="checkbox" checked={!!editAgentData.is_active} onChange={e => { const checked = e.target.checked; setEditAgentData(d => ({ ...d, is_active: checked })); }} />
                            Actif
                          </label>
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button onClick={saveEditAgent} disabled={saving} title="Sauvegarder" style={{ padding: '5px 10px', background: '#28A745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button onClick={cancelEditAgent} title="Annuler" style={{ padding: '5px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...sTd, textAlign: 'right' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '1rem', color: '#212529' }}>
                            {limit.number_limit !== null && limit.number_limit !== undefined
                              ? `${Number(limit.number_limit).toLocaleString()} HTG`
                              : <span style={{ color: '#999', fontStyle: 'italic', fontWeight: '400' }}>N/A</span>
                            }
                          </span>
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: limit.is_active ? '#28A745' : '#dc3545' }} />
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button onClick={() => startEditAgent(limit)} title="Modifier" style={{ padding: '5px 9px', background: 'none', border: '1px solid #dee2e6', borderRadius: '4px', color: '#555', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                            <button onClick={() => handleDeleteAgent(limit.id)} title="Supprimer" style={{ padding: '5px 9px', background: 'none', border: '1px solid #dc3545', borderRadius: '4px', color: '#dc3545', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LIMITE BOUL PAR FICHE                                      */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === 'fiche' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ fontWeight: '700', margin: 0 }}>Limite Boul par Fiche</h4>
            <button onClick={() => setShowAddFiche(v => !v)} style={{ padding: '6px 14px', background: showAddFiche ? '#6c757d' : '#DC3545', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FontAwesomeIcon icon={showAddFiche ? faTimes : faPlus} />
              {showAddFiche ? 'Annuler' : 'Ajouter'}
            </button>
          </div>

          {showAddFiche && (
            <form onSubmit={handleAddFicheLimit} style={{ background: '#f8f9fa', border: '1px solid #e9ecef', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'end' }}>
                <div>
                  <label style={sLabel}>Jeu</label>
                  <select style={sSelect} value={newFicheLimit.game_type} onChange={e => { const val = e.target.value; setNewFicheLimit(f => ({ ...f, game_type: val })); }}>
                    {GAME_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={sLabel}>Tirage (opt.)</label>
                  <select style={sSelect} value={newFicheLimit.draw} onChange={e => { const val = e.target.value; setNewFicheLimit(f => ({ ...f, draw: val })); }}>
                    <option value="">Tous les tirages</option>
                    {draws.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={sLabel}>Max (HTG) *</label>
                  <input type="number" style={sInput} value={newFicheLimit.max_bet} onChange={e => { const val = e.target.value; setNewFicheLimit(f => ({ ...f, max_bet: val })); }} placeholder="5000" required />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="submit" disabled={saving} style={{ width: '100%', padding: '8px', background: '#DC3545', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: '600', fontSize: '0.88rem', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />Enregistrer
                  </button>
                </div>
              </div>
            </form>
          )}

          <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={sTh}>Jeu</th>
                  <th style={sTh}>Tirage</th>
                  <th style={{ ...sTh, textAlign: 'right' }}>Min (HTG)</th>
                  <th style={{ ...sTh, textAlign: 'right' }}>Max (HTG)</th>
                  <th style={{ ...sTh, textAlign: 'center' }}>Statut</th>
                  <th style={{ ...sTh, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ficheLimits.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...sTd, textAlign: 'center', color: '#999', padding: '28px' }}>Aucune limite définie.</td></tr>
                ) : ficheLimits.map(limit => (
                  <tr key={limit.id} style={{ background: editingFiche === limit.id ? '#fff5f5' : '#fff' }}>
                    <td style={sTd}>{limit.game_type_display || limit.game_type}</td>
                    <td style={{ ...sTd, color: '#666' }}>{limit.draw_name || 'Tous'}</td>

                    {editingFiche === limit.id ? (
                      <>
                        <td style={sTd}>
                          <input type="number" value={editFicheData.min_bet ?? ''} onChange={e => { const val = e.target.value; setEditFicheData(d => ({ ...d, min_bet: val })); }} style={{ ...sInput, width: '90px', textAlign: 'right' }} />
                        </td>
                        <td style={sTd}>
                          <input type="number" value={editFicheData.max_bet ?? ''} onChange={e => { const val = e.target.value; setEditFicheData(d => ({ ...d, max_bet: val })); }} style={{ ...sInput, width: '90px', textAlign: 'right' }} />
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                            <input type="checkbox" checked={!!editFicheData.is_active} onChange={e => { const checked = e.target.checked; setEditFicheData(d => ({ ...d, is_active: checked })); }} />
                            Actif
                          </label>
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button onClick={saveEditFiche} disabled={saving} style={{ padding: '5px 10px', background: '#28A745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button onClick={cancelEditFiche} style={{ padding: '5px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...sTd, textAlign: 'right', fontFamily: 'monospace' }}>{Number(limit.min_bet).toLocaleString()}</td>
                        <td style={{ ...sTd, textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}>{Number(limit.max_bet).toLocaleString()}</td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: limit.is_active ? '#28A745' : '#dc3545' }} />
                        </td>
                        <td style={{ ...sTd, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button onClick={() => startEditFiche(limit)} style={{ padding: '5px 9px', background: 'none', border: '1px solid #dee2e6', borderRadius: '4px', color: '#555', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                            <button onClick={() => handleDeleteFiche(limit.id)} style={{ padding: '5px 9px', background: 'none', border: '1px solid #dc3545', borderRadius: '4px', color: '#dc3545', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
